import { getStockTemplates, getTemplates } from "@/sideEffects/api";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AxDataTableRequestInput, Template } from "@/lib/types";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { convertToTemplate } from "../utils/templateUtils";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TemplatePreview } from "./TemplatePreview";

interface Props {
  onSelectTemplate: (template: Template) => void;
  onClose: () => void;
  selectedTemplateId?: string;
}

type TabType = "my-templates" | "template-gallery";

// Interface for grouped templates by category
interface GroupedTemplates {
  [category: string]: Template[];
}

export const TemplateSelector: React.FC<Props> = ({ onSelectTemplate, onClose, selectedTemplateId }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [activeTab, setActiveTab] = useState<TabType>("my-templates");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [input] = useState<AxDataTableRequestInput>({
    search: "",
    pageNumber: 1,
    numberOfObjectsPerPage: 9999,
  });

  const { data, isLoading: isTemplatesLoading } = useQuery({
    queryKey: ["getTemplates", activeTab],
    // @ts-ignore typing error for now, it's about firmGuid and can be ignored
    queryFn: () => {
      if (activeTab === "my-templates") {
        return getTemplates(input, { FirmGuid: activeFirm?.firmGuid });
      } else {
        return getStockTemplates(input);
      }
    },
    refetchOnMount: true,
  });

  const templates = data?.data?.result?.map((template) => convertToTemplate(template)) || [];
  const filteredTemplates = templates.filter(
    (template) =>
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.viewport_size.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (template.categoryName && template.categoryName.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Group templates by category
  const groupTemplatesByCategory = (templates: Template[]): GroupedTemplates => {
    return templates.reduce((acc: GroupedTemplates, template) => {
      const category = template.categoryName || t("template.uncategorized");
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(template);
      return acc;
    }, {});
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    // Implement search logic here if needed
    setIsSearching(false);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const renderTemplateGrid = (templates: Template[]) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      {templates.map((template) => (
        <div
          key={template.id}
          className={cn(
            "group relative overflow-hidden bg-card rounded-lg border shadow-sm transition-all duration-200",
            "hover:shadow-md hover:scale-[1.02]",
            selectedTemplateId === template.id && "border-2 border-primary"
          )}
          onClick={() => onSelectTemplate(template)}
        >
          <div className="p-4">
            <div className="relative mb-4 py-6 overflow-hidden rounded-md border bg-muted/50">
              <TemplatePreview data={template.canvas_data} previewSize="small" />
            </div>
            <h3 className="font-semibold text-lg truncate">{template.name}</h3>
            <p className="text-sm text-muted-foreground">
              {template.categoryName && `${template.categoryName} - `}
              {template.viewport_size}
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  // Render templates grouped by category
  const renderGroupedTemplateGrid = (templates: Template[]) => {
    const groupedTemplates = groupTemplatesByCategory(templates);
    const uncategorizedKey = t("common.uncategorized");

    return (
      <div className="space-y-8">
        {Object.entries(groupedTemplates)
          .sort(([categoryA], [categoryB]) => {
            // Always put "Uncategorized" at the end
            if (categoryA === uncategorizedKey) return 1;
            if (categoryB === uncategorizedKey) return -1;
            return categoryA.localeCompare(categoryB);
          })
          .map(([category, templates]) => (
            <div key={category} className="space-y-4">
              <h2 className="text-xl font-semibold border-b pb-2">{category}</h2>
              {renderTemplateGrid(templates)}
            </div>
          ))}
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[80vw] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("template.selectTemplate")}</DialogTitle>
        </DialogHeader>
        <LoadingOverlay isLoading={isTemplatesLoading}>
          <Tabs defaultValue="my-templates" className="mt-6" onValueChange={(value) => setActiveTab(value as TabType)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="my-templates">{t("template.myTemplates")}</TabsTrigger>
              <TabsTrigger value="template-gallery">{t("template.templateGallery")}</TabsTrigger>
            </TabsList>

            <div className="my-6">
              <form onSubmit={handleSearch} className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                  <Input
                    type="text"
                    placeholder={t("template.searchPlaceholder")}
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    className="pl-9 pr-4"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="absolute right-2.5 top-2.5"
                      aria-label={t("content.clear")}
                    >
                      <X className="h-4 w-4 text-gray-500" />
                    </button>
                  )}
                </div>
                <Button type="submit" disabled={isSearching}>
                  {isSearching ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  {t("common.search")}
                </Button>
              </form>
            </div>

            <TabsContent value="my-templates" className="mt-0">
              <ScrollArea className="h-[70vh]">{renderTemplateGrid(filteredTemplates)}</ScrollArea>
            </TabsContent>

            <TabsContent value="template-gallery" className="mt-0">
              <ScrollArea className="h-[70vh]">{renderGroupedTemplateGrid(filteredTemplates)}</ScrollArea>
            </TabsContent>
          </Tabs>
        </LoadingOverlay>
      </DialogContent>
    </Dialog>
  );
};
